/**
 * groundKits.ts — BETA 23.60 · PROCEDURAL GROUND MATERIAL KITS
 * (Dylan's procedural-ground-kits pack: "read from the ground first, props
 * second. A junkyard should read as a junkyard before you notice a car.")
 *
 * Each PLACE (paths.ts) owns a ground kit. This module paints it in LAYERS
 * into the chunk ground cache, keyed by the place's GroundKitId:
 *   1. SOFT BASE — a radial material fill with an alpha FALLOFF at the rim
 *      (the "transition band" — no hard ellipse edge; the module bleeds into
 *      the forest floor like the pack demands)
 *   2. MATERIAL NOISE — seeded speckle of a second palette tone
 *   3. DECAL FAMILIES — per-kit clustered stains/tracks/chips/cracks, seeded,
 *      varied in scale/alpha/rotation, thinned in the walkable center so
 *      enemy/projectile silhouettes stay readable
 *
 * Deterministic per place+seed; the decal list is generated once and cached,
 * then each chunk draws only the decals inside its bounds (seamless across
 * chunk borders, same trick as surge veins / the forest hem).
 */

import { cellRng } from './mapChunks';
import type { GroundKitId, MachineStation } from './paths';

// ── decal primitives ─────────────────────────────────────────────────────────
type DecalKind = 'blob' | 'speck' | 'tire' | 'slab' | 'crack' | 'chip' | 'fog';
interface GroundDecal {
  kind: DecalKind;
  x: number; y: number;      // world coords
  r: number;                 // world radius / half-size
  rot: number;
  color: string;
  alpha: number;
}

interface KitDef {
  displayName: string;
  /** base material fill color (rgb) + peak alpha at the core */
  base: [number, number, number];
  baseAlpha: number;
  /** how far the material reaches before fading (fraction of radius) */
  coreFrac: number;
  build: (push: PushDecal, rng: () => number, cx: number, cy: number, rx: number, ry: number) => void;
}
type PushDecal = (d: Omit<GroundDecal, 'x' | 'y'> & { fx: number; fy: number }) => void;

// helper: place a decal at fractional offset (fx,fy in -1..1 of the ellipse)
function makePush(cx: number, cy: number, rx: number, ry: number, list: GroundDecal[]): PushDecal {
  return (d) => {
    list.push({ kind: d.kind, x: cx + d.fx * rx, y: cy + d.fy * ry, r: d.r, rot: d.rot, color: d.color, alpha: d.alpha });
  };
}
// seeded point inside the ellipse, biased AWAY from the readable center
function scatter(rng: () => number, edgeBias = 0.35): { fx: number; fy: number; edge: number } {
  const a = rng() * Math.PI * 2;
  const rr = Math.sqrt(edgeBias + rng() * (1 - edgeBias));   // pushes toward the rim
  return { fx: Math.cos(a) * rr * 0.95, fy: Math.sin(a) * rr * 0.95, edge: rr };
}
// 23.61 — seeded point in a RING of the ellipse (rim band, or just past it
// when hi > 1: the bleed onto the surrounding forest floor)
function rimPt(rng: () => number, lo: number, hi: number): { fx: number; fy: number; a: number } {
  const a = rng() * Math.PI * 2;
  const rr = lo + rng() * (hi - lo);
  return { fx: Math.cos(a) * rr, fy: Math.sin(a) * rr, a };
}

const KITS: Record<GroundKitId, KitDef> = {
  // ── JUNKYARD: oil-stained dirt, gravel, tire tracks, rust, concrete, scrap ──
  junkyard_floor: {
    displayName: 'junkyard_oil_gravel', base: [56, 52, 46], baseAlpha: 0.40, coreFrac: 0.72,
    build: (push, rng) => {
      for (let i = 0; i < 40; i++) { const s = scatter(rng, 0.1); push({ kind: 'speck', fx: s.fx, fy: s.fy, r: 3 + rng() * 6, rot: 0, color: '124,116,102', alpha: 0.10 + rng() * 0.12 }); }   // gravel
      for (let i = 0; i < 7;  i++) { const s = scatter(rng, 0.2); push({ kind: 'blob',  fx: s.fx, fy: s.fy, r: 26 + rng() * 46, rot: rng() * Math.PI, color: '10,8,6', alpha: 0.22 + rng() * 0.18 }); }   // oil
      for (let i = 0; i < 6;  i++) { const s = scatter(rng, 0.2); push({ kind: 'blob',  fx: s.fx, fy: s.fy, r: 30 + rng() * 40, rot: rng() * Math.PI, color: '86,52,30', alpha: 0.14 + rng() * 0.12 }); }   // rust dirt
      for (let i = 0; i < 5;  i++) { const s = scatter(rng, 0.3); push({ kind: 'tire',  fx: s.fx, fy: s.fy, r: 90 + rng() * 100, rot: rng() * Math.PI, color: '18,15,11', alpha: 0.26 + rng() * 0.14 }); }
      for (let i = 0; i < 8;  i++) { const s = scatter(rng, 0.4); push({ kind: 'slab',  fx: s.fx, fy: s.fy, r: 14 + rng() * 20, rot: rng() * Math.PI, color: '120,116,110', alpha: 0.14 + rng() * 0.10 }); }   // concrete chunks
      for (let i = 0; i < 22; i++) { const s = scatter(rng, 0.3); push({ kind: 'chip',  fx: s.fx, fy: s.fy, r: 2 + rng() * 3, rot: rng() * Math.PI, color: '150,144,132', alpha: 0.14 + rng() * 0.12 }); }   // metal bits
    },
  },
  // ── NEIGHBORHOOD: cracked road, driveways, sidewalks, dead lawn, leaves ──
  yard: {
    displayName: 'neighborhood_ruined', base: [72, 70, 44], baseAlpha: 0.26, coreFrac: 0.78,
    build: (push, rng, cx, cy, rx, ry) => {
      // a cracked road strip across the middle
      const roadY = (rng() - 0.5) * 0.3;
      for (let t = -0.85; t <= 0.85; t += 0.12) push({ kind: 'slab', fx: t, fy: roadY + (rng() - 0.5) * 0.06, r: 34 + rng() * 10, rot: 0, color: '58,56,54', alpha: 0.30 + rng() * 0.10 });
      for (let i = 0; i < 5; i++) { const s = scatter(rng, 0.35); push({ kind: 'slab', fx: s.fx, fy: s.fy, r: 22 + rng() * 14, rot: rng() * 0.4, color: '96,92,86', alpha: 0.22 + rng() * 0.10 }); }   // driveway/sidewalk slabs
      for (let i = 0; i < 10; i++) { const s = scatter(rng, 0.15); push({ kind: 'blob', fx: s.fx, fy: s.fy, r: 20 + rng() * 30, rot: rng() * Math.PI, color: '58,48,30', alpha: 0.14 + rng() * 0.12 }); }   // dead-lawn mud
      for (let i = 0; i < 30; i++) { const s = scatter(rng, 0.1);  push({ kind: 'chip', fx: s.fx, fy: s.fy, r: 2 + rng() * 3, rot: rng() * Math.PI, color: '120,84,40', alpha: 0.12 + rng() * 0.12 }); }   // leaves
      for (let i = 0; i < 5; i++) { const s = scatter(rng, 0.3);  push({ kind: 'crack', fx: s.fx, fy: s.fy, r: 34 + rng() * 30, rot: rng() * Math.PI, color: '30,28,26', alpha: 0.30 }); }   // road cracks
      void cx; void cy; void rx; void ry;
    },
  },
  // ── GRAVEYARD: stone paths, grave dirt, moss, dead grass, fog pools ──
  grave_soil: {
    displayName: 'graveyard_damp_stone', base: [40, 40, 38], baseAlpha: 0.34, coreFrac: 0.76,
    build: (push, rng) => {
      for (let i = 0; i < 6; i++) { const s = scatter(rng, 0.3); push({ kind: 'slab', fx: s.fx, fy: s.fy, r: 18 + rng() * 16, rot: rng() * Math.PI, color: '108,106,100', alpha: 0.18 + rng() * 0.10 }); }   // stone path chips
      for (let i = 0; i < 10; i++) { const s = scatter(rng, 0.2); push({ kind: 'blob', fx: s.fx, fy: s.fy, r: 16 + rng() * 22, rot: rng() * Math.PI, color: '30,30,24', alpha: 0.20 + rng() * 0.12 }); }   // grave dirt mounds
      for (let i = 0; i < 12; i++) { const s = scatter(rng, 0.2); push({ kind: 'blob', fx: s.fx, fy: s.fy, r: 10 + rng() * 16, rot: rng() * Math.PI, color: '46,64,34', alpha: 0.12 + rng() * 0.10 }); }   // moss
      for (let i = 0; i < 3; i++) { const s = scatter(rng, 0.4); push({ kind: 'fog', fx: s.fx * 0.6, fy: s.fy * 0.6, r: 60 + rng() * 60, rot: 0, color: '150,158,150', alpha: 0.06 + rng() * 0.05 }); }   // fog pools
    },
  },
  // ── CAMP: trampled dirt, ash, footprints, straw, fire-pit dirt ──
  camp_ground: {
    displayName: 'camp_trampled_ash', base: [92, 80, 62], baseAlpha: 0.26, coreFrac: 0.7,
    build: (push, rng) => {
      push({ kind: 'blob', fx: 0, fy: 0, r: 40, rot: 0, color: '58,54,50', alpha: 0.38 });   // ash heart at the fire
      push({ kind: 'blob', fx: 0, fy: 0, r: 22, rot: 0, color: '24,20,18', alpha: 0.40 });   // fire-pit char
      for (let i = 0; i < 16; i++) { const s = scatter(rng, 0.15); push({ kind: 'blob', fx: s.fx, fy: s.fy, r: 5 + rng() * 8, rot: rng() * Math.PI, color: '50,44,38', alpha: 0.12 + rng() * 0.10 }); }   // trampled patches
      for (let i = 0; i < 24; i++) { const s = scatter(rng, 0.1);  push({ kind: 'chip', fx: s.fx, fy: s.fy, r: 2 + rng() * 3, rot: rng() * Math.PI, color: '150,132,80', alpha: 0.12 + rng() * 0.10 }); }   // straw flecks
    },
  },
  // ── CRASH SITE: burned dirt, scorch, fuel stains, debris trail, ash ──
  scorched: {
    displayName: 'crash_burned_debris', base: [22, 18, 14], baseAlpha: 0.52, coreFrac: 0.62,
    build: (push, rng, cx, cy, rx) => {
      for (let i = 0; i < 10; i++) { const s = scatter(rng, 0.2); push({ kind: 'blob', fx: s.fx, fy: s.fy, r: 14 + rng() * 30, rot: rng() * Math.PI, color: '8,6,5', alpha: 0.22 + rng() * 0.18 }); }   // scorch
      for (let i = 0; i < 5;  i++) { const s = scatter(rng, 0.3); push({ kind: 'blob', fx: s.fx, fy: s.fy, r: 12 + rng() * 20, rot: rng() * Math.PI, color: '40,44,30', alpha: 0.14 + rng() * 0.10 }); }   // fuel sheen
      // debris trail — a line of dark bits from the impact outward
      const ang = rng() * Math.PI * 2;
      for (let t = 0.1; t < 1.0; t += 0.08) push({ kind: 'chip', fx: Math.cos(ang) * t, fy: Math.sin(ang) * t, r: 2 + rng() * 4, rot: rng() * Math.PI, color: '20,17,13', alpha: 0.24 });
      for (let i = 0; i < 20; i++) { const s = scatter(rng, 0.3); push({ kind: 'chip', fx: s.fx, fy: s.fy, r: 1.5 + rng() * 3, rot: rng() * Math.PI, color: '120,112,100', alpha: 0.12 + rng() * 0.10 }); }   // ash flecks
      void cx; void cy; void rx;
    },
  },
  // ── CORRUPTION: blackened dirt, purple cracks, crystal dust ──
  blackened: {
    displayName: 'corruption_black_vein', base: [12, 8, 14], baseAlpha: 0.5, coreFrac: 0.7,
    build: (push, rng) => {
      for (let i = 0; i < 6; i++) { const s = scatter(rng, 0.2); push({ kind: 'crack', fx: s.fx, fy: s.fy, r: 30 + rng() * 40, rot: rng() * Math.PI, color: '150,50,220', alpha: 0.30 + rng() * 0.16 }); }   // purple cracks
      for (let i = 0; i < 24; i++) { const s = scatter(rng, 0.2); push({ kind: 'chip', fx: s.fx, fy: s.fy, r: 1.5 + rng() * 2.5, rot: rng() * Math.PI, color: '190,120,240', alpha: 0.14 + rng() * 0.12 }); }   // crystal dust
    },
  },
  // ── ARENA / CHECKPOINT: compacted worn earth (readable combat floor) ──
  compacted: {
    displayName: 'compacted_worn', base: [74, 68, 58], baseAlpha: 0.20, coreFrac: 0.8,
    build: (push, rng) => {
      for (let i = 0; i < 16; i++) { const s = scatter(rng, 0.2); push({ kind: 'speck', fx: s.fx, fy: s.fy, r: 3 + rng() * 5, rot: 0, color: '110,102,90', alpha: 0.08 + rng() * 0.08 }); }
      for (let i = 0; i < 4; i++) { const s = scatter(rng, 0.3); push({ kind: 'blob', fx: s.fx, fy: s.fy, r: 18 + rng() * 24, rot: rng() * Math.PI, color: '52,48,40', alpha: 0.10 + rng() * 0.08 }); }
    },
  },
};

export function groundKitDisplayName(id: GroundKitId): string {
  return KITS[id]?.displayName ?? id;
}

// ── 23.61 · GROUND PASS 2: BIOME-BLEND RIM ──────────────────────────────────
// The forest leans IN (leaf litter, twigs, roots and moss scattered over the
// kit's rim band) and the kit leaks OUT (its own material dusted past the rim
// onto the forest floor) — leaves-in-scrap, roots-in-gravel: every module
// reads knitted into the woods instead of placed on top of them.
interface BlendSpec { leaves: [string, string]; roots: number; moss?: string; out: [string, string] }
const EDGE_BLENDS: Record<GroundKitId, BlendSpec> = {
  junkyard_floor: { leaves: ['120,84,40', '92,66,32'],  roots: 4, out: ['124,116,102', '86,52,30'] },
  yard:           { leaves: ['120,84,40', '100,90,44'], roots: 3, moss: '64,66,38', out: ['58,48,30', '96,92,86'] },
  grave_soil:     { leaves: ['70,58,30', '54,48,26'],   roots: 5, moss: '46,64,34', out: ['30,30,24', '46,64,34'] },
  camp_ground:    { leaves: ['120,84,40', '110,92,50'], roots: 3, out: ['150,132,80', '50,44,38'] },
  scorched:       { leaves: ['48,36,22', '36,28,18'],   roots: 2, out: ['120,112,100', '10,8,6'] },
  blackened:      { leaves: ['52,40,28', '38,26,30'],   roots: 3, out: ['190,120,240', '14,9,16'] },
  compacted:      { leaves: ['110,86,44', '90,74,40'],  roots: 2, out: ['110,102,90', '74,68,58'] },
};

function edgeBlend(kitId: GroundKitId, push: PushDecal, rng: () => number): void {
  const spec = EDGE_BLENDS[kitId];
  if (!spec) return;
  // forest → kit: leaf litter drifted over the rim band
  for (let i = 0; i < 16; i++) {
    const p = rimPt(rng, 0.78, 1.0);
    push({ kind: 'chip', fx: p.fx, fy: p.fy, r: 2 + rng() * 3.5, rot: rng() * Math.PI, color: spec.leaves[i & 1], alpha: 0.12 + rng() * 0.14 });
  }
  // fallen twigs
  for (let i = 0; i < 4; i++) {
    const p = rimPt(rng, 0.8, 1.0);
    push({ kind: 'crack', fx: p.fx, fy: p.fy, r: 8 + rng() * 8, rot: rng() * Math.PI, color: '30,24,14', alpha: 0.30 });
  }
  // roots crawling INWARD from the treeline
  for (let i = 0; i < spec.roots; i++) {
    const p = rimPt(rng, 0.92, 1.02);
    push({ kind: 'crack', fx: p.fx, fy: p.fy, r: 16 + rng() * 14, rot: p.a + Math.PI + (rng() - 0.5) * 0.7, color: '38,30,18', alpha: 0.28 + rng() * 0.10 });
  }
  // damp kits grow moss where the shade starts
  if (spec.moss) for (let i = 0; i < 6; i++) {
    const p = rimPt(rng, 0.8, 1.0);
    push({ kind: 'blob', fx: p.fx, fy: p.fy, r: 6 + rng() * 10, rot: rng() * Math.PI, color: spec.moss, alpha: 0.10 + rng() * 0.08 });
  }
  // kit → forest: material dusted past the rim onto the forest floor
  for (let i = 0; i < 12; i++) {
    const p = rimPt(rng, 1.0, 1.2);
    push({ kind: i & 1 ? 'speck' : 'chip', fx: p.fx, fy: p.fy, r: 2 + rng() * 4, rot: rng() * Math.PI, color: spec.out[i & 1], alpha: 0.10 + rng() * 0.12 });
  }
}

// ── per-place decal cache (deterministic) ────────────────────────────────────
interface PlaceLike { x: number; y: number; rx: number; ry: number; groundKit: GroundKitId }
const _decalCache = new Map<string, GroundDecal[]>();

function placeDecals(pl: PlaceLike, seed: number): GroundDecal[] {
  const key = `${Math.round(pl.x)},${Math.round(pl.y)},${seed}`;
  const hit = _decalCache.get(key);
  if (hit) return hit;
  if (_decalCache.size > 400) _decalCache.clear();
  const kit = KITS[pl.groundKit];
  const list: GroundDecal[] = [];
  if (kit) {
    const rng = cellRng(Math.round(pl.x / 96), Math.round(pl.y / 96), seed, 6161);
    const push = makePush(pl.x, pl.y, pl.rx * 0.92, pl.ry * 0.92, list);
    kit.build(push, rng, pl.x, pl.y, pl.rx, pl.ry);
    edgeBlend(pl.groundKit, push, rng);   // 23.61 — biome-blend rim
  }
  _decalCache.set(key, list);
  return list;
}

/**
 * Paint a place's ground kit into a chunk canvas. Call after paths bake,
 * before underbrush. `lx/ly` map world→local px; GROUND_SQUASH handled by ry.
 */
export function paintPlaceGround(
  c: CanvasRenderingContext2D,
  pl: PlaceLike, seed: number,
  lx: (wx: number) => number, ly: (wy: number) => number,
  pxPerWu: number,
): void {
  const kit = KITS[pl.groundKit];
  if (!kit) return;
  const px = lx(pl.x), py = ly(pl.y);
  const rxp = pl.rx * pxPerWu, ryp = pl.ry * pxPerWu;
  const [r, g, b] = kit.base;

  // ── LAYER 1: soft base fill — radial alpha FALLOFF is the transition band
  // (material solid to coreFrac, fading to 0 at the rim → bleeds into forest)
  const grad = c.createRadialGradient(px, py, rxp * 0.05, px, py, rxp);
  grad.addColorStop(0, `rgba(${r},${g},${b},${kit.baseAlpha})`);
  grad.addColorStop(kit.coreFrac, `rgba(${r},${g},${b},${kit.baseAlpha * 0.7})`);
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
  c.save();
  c.translate(px, py); c.scale(1, ryp / rxp); c.translate(-px, -py);   // squash to the ellipse
  c.fillStyle = grad;
  c.beginPath();
  c.arc(px, py, rxp, 0, Math.PI * 2);
  c.fill();
  c.restore();

  // ── LAYERS 2-3: decals (draw only those near this chunk) ──
  const decals = placeDecals(pl, seed);
  for (const d of decals) {
    const dx = lx(d.x), dy = ly(d.y);
    const rp = d.r * pxPerWu;
    // chunk-bounds cull — RADIUS-AWARE (23.61 fix: a fixed 80px pad culled
    // the biggest decals — 190wu tire arcs — while their edge still reached
    // this chunk → popped at chunk borders)
    const pad = 80 + rp;
    if (dx < -pad || dy < -pad || dx > (c.canvas.width) + pad || dy > (c.canvas.height) + pad) continue;
    c.save();
    if (d.kind === 'speck' || d.kind === 'blob' || d.kind === 'chip') {
      c.fillStyle = `rgba(${d.color},${d.alpha})`;
      c.beginPath();
      c.ellipse(dx, dy, rp, rp * (d.kind === 'chip' ? 0.7 : 0.55), d.rot, 0, Math.PI * 2);
      c.fill();
    } else if (d.kind === 'slab') {
      c.translate(dx, dy); c.rotate(d.rot);
      c.fillStyle = `rgba(${d.color},${d.alpha})`;
      c.fillRect(-rp, -rp * 0.55, rp * 2, rp * 1.1);
    } else if (d.kind === 'fog') {
      const fg = c.createRadialGradient(dx, dy, rp * 0.1, dx, dy, rp);
      fg.addColorStop(0, `rgba(${d.color},${d.alpha})`);
      fg.addColorStop(1, `rgba(${d.color},0)`);
      c.fillStyle = fg;
      c.beginPath(); c.ellipse(dx, dy, rp, rp * 0.5, 0, 0, Math.PI * 2); c.fill();
    } else if (d.kind === 'tire') {
      c.strokeStyle = `rgba(${d.color},${d.alpha})`;
      c.lineWidth = 2.2;
      for (const off of [-3.4, 3.4]) {
        c.beginPath();
        c.arc(dx + Math.cos(d.rot + Math.PI / 2) * off, dy + Math.sin(d.rot + Math.PI / 2) * off * 0.55, rp, d.rot - 0.7, d.rot + 0.7);
        c.stroke();
      }
    } else if (d.kind === 'crack') {
      // short jagged fracture (dark for road cracks, purple for corruption)
      c.strokeStyle = `rgba(${d.color},${d.alpha})`;
      c.lineWidth = 1.6;
      c.beginPath();
      let ax = dx, ay = dy, ang = d.rot;
      c.moveTo(ax, ay);
      for (let s = 0; s < 5; s++) { ang += (((d.x + s) * 2.3) % 1 - 0.5) * 1.6; ax += Math.cos(ang) * rp * 0.28; ay += Math.sin(ang) * rp * 0.28 * 0.55; c.lineTo(ax, ay); }
      c.stroke();
    }
    c.restore();
  }
}

// ── 23.61 · STATION PADS → 23.68 ORGANIC REWRITE (Dylan's live review §3b S5)
// The 23.61 concrete slab read as a PERFECT SQUARE — Dylan: "out of place...
// should just be a very specific texture layered with code under it. like
// gravel... just like you did the paths but even more randomized. no sharp
// corners." Every pad is now an ORGANIC BLOB: one seeded irregular outline
// (16-point wobble, smoothed), a soft fade rim that sinks into the ground
// (no proud lip, no perimeter strokes, zero straight edges), per-kind material:
//   roadside → packed GRAVEL (dense stone speckle, lit-SW pebbles, oil wear)
//   booth    → oil-dark junkyard grit (oil pools + scrap flakes — plates gone)
//   kiosk    → packed camp dirt (straw flecks + trample tones — planks gone)
export function paintStationPad(
  c: CanvasRenderingContext2D,
  st: MachineStation, seed: number,
  lx: (wx: number) => number, ly: (wy: number) => number,
  pxPerWu: number, squash: number,
): void {
  const rng = cellRng(Math.round(st.x / 64), Math.round(st.y / 64), seed, 4242);
  const px = lx(st.x), py = ly(st.y + 20);   // pad center sits a touch south (interaction side)
  const hw = (st.kind === 'roadside' ? 165 : st.kind === 'booth' ? 150 : 140) * pxPerWu;
  const hh = (st.kind === 'roadside' ? 280 : st.kind === 'booth' ? 260 : 240) * pxPerWu;   // pre-squash

  // ONE seeded irregular outline, reused at every scale (fade rim / core / clip)
  // so the passes always nest — never a second wobble that pokes out.
  const N = 16;
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    const r = 0.74 + rng() * 0.30;   // per-point wobble — the no-sharp-corners law
    pts.push({ x: Math.cos(a) * hw * r, y: Math.sin(a) * hh * r });
  }
  const blob = (s: number) => {
    c.beginPath();
    c.moveTo((pts[N - 1].x + pts[0].x) / 2 * s, (pts[N - 1].y + pts[0].y) / 2 * s);
    for (let i = 0; i < N; i++) {
      const p = pts[i], q = pts[(i + 1) % N];
      c.quadraticCurveTo(p.x * s, p.y * s, (p.x + q.x) / 2 * s, (p.y + q.y) / 2 * s);
    }
    c.closePath();
  };

  const PAL = st.kind === 'roadside'
    ? { fade: 'rgba(84,78,68,0.30)', base: 'rgba(88,82,72,0.80)', tone: ['rgba(74,68,58,', 'rgba(101,94,82,'] }
    : st.kind === 'booth'
      ? { fade: 'rgba(40,37,32,0.28)', base: 'rgba(44,41,36,0.78)', tone: ['rgba(34,31,26,', 'rgba(58,54,47,'] }
      : { fade: 'rgba(84,72,54,0.26)', base: 'rgba(90,78,58,0.72)', tone: ['rgba(74,63,45,', 'rgba(103,89,64,'] };

  c.save();
  c.translate(px, py);
  c.scale(1, squash);

  // the pad SINKS into the surroundings: soft fade ring, then the core body
  c.fillStyle = PAL.fade; blob(1); c.fill();
  c.fillStyle = PAL.base; blob(0.86); c.fill();

  // interior detail stays inside the core
  blob(0.86); c.clip();

  // tonal patches so the surface never reads flat (all kinds)
  for (let i = 0; i < 6; i++) {
    c.fillStyle = `${PAL.tone[i & 1]}${(0.14 + rng() * 0.13).toFixed(2)})`;
    const bx = (rng() - 0.5) * 1.5 * hw, by = (rng() - 0.5) * 1.6 * hh;
    const br = (24 + rng() * 38) * pxPerWu;
    c.beginPath(); c.ellipse(bx, by, br, br * 0.75, rng() * Math.PI, 0, Math.PI * 2); c.fill();
  }

  if (st.kind === 'roadside') {
    // GRAVEL — dense small stone speckle
    for (let i = 0; i < 46; i++) {
      const g = 96 + Math.floor(rng() * 44);
      c.fillStyle = `rgba(${g},${g - 6},${g - 16},${(0.16 + rng() * 0.20).toFixed(2)})`;
      const sx = (rng() - 0.5) * 1.7 * hw, sy = (rng() - 0.5) * 1.7 * hh;
      const sr = (1.2 + rng() * 2.4) * pxPerWu;
      c.beginPath(); c.ellipse(sx, sy, sr, sr * 0.8, rng() * Math.PI, 0, Math.PI * 2); c.fill();
    }
    // a few larger pebbles, lit SW per the light law (shadow copy NE-side first)
    for (let i = 0; i < 7; i++) {
      const sx = (rng() - 0.5) * 1.4 * hw, sy = (rng() - 0.5) * 1.5 * hh;
      const sr = (3 + rng() * 4) * pxPerWu, rot = rng() * Math.PI;
      c.fillStyle = 'rgba(58,53,45,0.5)';
      c.beginPath(); c.ellipse(sx + 1.2 * pxPerWu, sy - 1.2 * pxPerWu, sr, sr * 0.7, rot, 0, Math.PI * 2); c.fill();
      c.fillStyle = `rgba(${118 + (rng() * 26 | 0)},${110 + (rng() * 22 | 0)},96,0.55)`;
      c.beginPath(); c.ellipse(sx, sy, sr, sr * 0.7, rot, 0, Math.PI * 2); c.fill();
    }
    // oil wear where the machine stands (north half)
    const og = c.createRadialGradient(0, -hh * 0.35, 4, 0, -hh * 0.35, 58 * pxPerWu);
    og.addColorStop(0, 'rgba(12,10,8,0.30)'); og.addColorStop(1, 'rgba(12,10,8,0)');
    c.fillStyle = og; c.fillRect(-hw * 2, -hh * 2, hw * 4, hh * 4);
    // walked-in approach (south) — a soft trample glow, not a strip
    const ag = c.createRadialGradient(0, hh * 0.5, 6, 0, hh * 0.5, 70 * pxPerWu);
    ag.addColorStop(0, 'rgba(128,120,106,0.14)'); ag.addColorStop(1, 'rgba(128,120,106,0)');
    c.fillStyle = ag; c.fillRect(-hw * 2, -hh * 2, hw * 4, hh * 4);
  } else if (st.kind === 'booth') {
    // oil pools + scrap-flake grit (the rectangular steel plates are gone)
    for (let i = 0; i < 3; i++) {
      c.fillStyle = `rgba(10,8,6,${(0.16 + rng() * 0.14).toFixed(2)})`;
      const bx = (rng() - 0.5) * 1.4 * hw, by = (rng() - 0.5) * 1.5 * hh;
      const br = (14 + rng() * 24) * pxPerWu;
      c.beginPath(); c.ellipse(bx, by, br, br * 0.7, rng() * Math.PI, 0, Math.PI * 2); c.fill();
    }
    for (let i = 0; i < 26; i++) {
      c.fillStyle = `rgba(${104 + (rng() * 30 | 0)},${98 + (rng() * 24 | 0)},86,${(0.10 + rng() * 0.14).toFixed(2)})`;
      const sx = (rng() - 0.5) * 1.7 * hw, sy = (rng() - 0.5) * 1.7 * hh;
      c.beginPath(); c.ellipse(sx, sy, (1 + rng() * 2) * pxPerWu, (0.8 + rng() * 1.6) * pxPerWu, rng() * Math.PI, 0, Math.PI * 2); c.fill();
    }
  } else {
    // kiosk: straw flecks + trample tones on packed dirt (plank edging gone)
    for (let i = 0; i < 22; i++) {
      c.strokeStyle = `rgba(150,128,84,${(0.14 + rng() * 0.16).toFixed(2)})`;
      c.lineWidth = 1;
      const sx = (rng() - 0.5) * 1.6 * hw, sy = (rng() - 0.5) * 1.6 * hh;
      const a = rng() * Math.PI, l = (3 + rng() * 5) * pxPerWu;
      c.beginPath(); c.moveTo(sx, sy); c.lineTo(sx + Math.cos(a) * l, sy + Math.sin(a) * l); c.stroke();
    }
    for (let i = 0; i < 3; i++) {
      c.fillStyle = `rgba(50,44,38,${(0.12 + rng() * 0.08).toFixed(2)})`;
      const bx = (rng() - 0.5) * 1.4 * hw, by = (rng() - 0.5) * 1.4 * hh;
      const br = (12 + rng() * 18) * pxPerWu;
      c.beginPath(); c.ellipse(bx, by, br, br * 0.6, rng() * Math.PI, 0, Math.PI * 2); c.fill();
    }
  }
  c.restore();
}

/** Clear the decal cache (floor advance / new world). */
export function clearGroundKitCache(): void { _decalCache.clear(); }
